/**
 * Tag Input Component
 * Allows users to enter multiple domain names as tags/badges
 * Enhanced with domain validation and smooth animations
 */

import { useState, KeyboardEvent, ChangeEvent, useEffect } from 'react'
import { X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { motion, AnimatePresence } from 'framer-motion'

interface TagInputProps {
  value: string[]
  onChange: (tags: string[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

// Domain validation regex
// Matches: google.com, example.org, saifchat.co.uk, abc.net.group, subdomain.example.com
// Excludes: asdf, a.b, -bad.com, example.
const DOMAIN_REGEX = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63}(?<!-))*\.[A-Za-z]{2,}$/

const validateDomain = (domain: string): boolean => {
  if (!domain || domain.trim().length === 0) {
    return false
  }
  return DOMAIN_REGEX.test(domain.trim())
}

export function TagInput({ value, onChange, placeholder, disabled, className }: TagInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [isInvalid, setIsInvalid] = useState(false)

  // Clear invalid state when input changes
  useEffect(() => {
    if (isInvalid) {
      const timer = setTimeout(() => {
        setIsInvalid(false)
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [isInvalid])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    if (
      (e.key === 'Enter' || e.key === ' ' || e.key === ',') &&
      inputValue.trim()
    ) {
      e.preventDefault()
      addTag(inputValue.trim())
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      removeTag(value.length - 1)
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    // Clear invalid state when user starts typing
    if (isInvalid) {
      setIsInvalid(false)
    }
  }

  const addTag = (tag: string) => {
    if (!tag || tag.trim().length === 0) {
      return
    }

    const trimmedTag = tag.trim()

    // Validate domain format
    if (!validateDomain(trimmedTag)) {
      setIsInvalid(true)
      toast.error('Please enter a valid domain (e.g. example.com)', {
        duration: 2000,
      })
      return
    }

    // Normalize tag (lowercase)
    const normalizedTag = trimmedTag.toLowerCase()

    // Check for duplicates (case-insensitive)
    if (value.some(existing => existing.toLowerCase() === normalizedTag)) {
      toast.error(`Domain "${normalizedTag}" is already added`, {
        duration: 2000,
      })
      return
    }

    // Add the valid domain tag
    onChange([...value, normalizedTag])
    setInputValue('')
    setIsInvalid(false)
  }

  const removeTag = (index: number) => {
    const newTags = value.filter((_, i) => i !== index)
    onChange(newTags)
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div
        className={cn(
          'flex min-h-10 flex-wrap gap-2 rounded-md border bg-background px-3 py-2 transition-colors',
          'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          isInvalid
            ? 'border-destructive focus-within:ring-destructive'
            : 'border-input',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {/* Display existing tags with animation */}
        <AnimatePresence mode="popLayout">
          {value.map((tag, index) => (
            <motion.div
              key={`${tag}-${index}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
            >
              <Badge
                variant="secondary"
                className="flex items-center gap-1 px-2 py-1 font-mono text-xs"
              >
                <span>{tag}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeTag(index)}
                    className="ml-1 rounded-full hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-destructive transition-colors"
                    aria-label={`Remove ${tag}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Input field */}
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          className={cn(
            'border-0 px-0 shadow-none focus:ring-0 focus-visible:ring-0 min-w-[120px] flex-1',
            isInvalid && 'text-destructive'
          )}
        />
      </div>
      {value.length === 0 && !isInvalid && (
        <p className="text-sm text-muted-foreground">
          Enter domain names and press space, comma, or enter to add them
        </p>
      )}
    </div>
  )
}